// admin/src/pages/GatewayMonitoring/components/LoginRelayCard.js
'use strict';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  getAuthRelayStatus,
  approveAuthRelayKey,
  revokeAuthRelayKey,
  extractGatewayErrorMessage,
} from '../gatewayApi';
import { FEATURES, hasFeatureAccess } from '../../../utils/rbac';

export default function LoginRelayCard({ currentUser, mode, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [relayData, setRelayData] = useState(null);
  const [selectedGatewayId, setSelectedGatewayId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [copied, setCopied] = useState(false);

  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [fingerprintInput, setFingerprintInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Permission check: strictly gated by hasFeatureAccess(currentUser, FEATURES.GATEWAYS, 'update') without role-name checks
  const canUpdate = Boolean(hasFeatureAccess(currentUser, FEATURES.GATEWAYS, 'update'));

  // Fetch status
  const fetchRelayStatus = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await getAuthRelayStatus();
      if (res && typeof res === 'object') {
        setRelayData(res);
        if (Array.isArray(res.gateways) && res.gateways.length > 0) {
          // Keep current selection if valid, otherwise pick first
          setSelectedGatewayId((prev) => {
            const exists = res.gateways.some((g) => g._id === prev);
            return exists ? prev : res.gateways[0]._id;
          });
        }
      }
    } catch (err) {
      setErrorMsg(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRelayStatus();
  }, [fetchRelayStatus]);

  // Active gateway object
  const activeGateway = useMemo(() => {
    if (!relayData?.gateways) return null;
    return relayData.gateways.find((g) => g._id === selectedGatewayId) || relayData.gateways[0] || null;
  }, [relayData, selectedGatewayId]);

  // Copy fingerprint helper
  const handleCopyFingerprint = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  // Open Approve Dialog
  const handleOpenApproveDialog = () => {
    const defaultFp = activeGateway?.relay?.fingerprint || '';
    setFingerprintInput(defaultFp);
    setApproveDialogOpen(true);
    setErrorMsg('');
    setSuccessMsg('');
  };

  // Submit Approval
  const handleConfirmApprove = async () => {
    if (!activeGateway?._id || !fingerprintInput.trim()) return;
    setActionLoading(true);
    try {
      await approveAuthRelayKey(activeGateway._id, fingerprintInput.trim());
      setSuccessMsg('Relay key approved successfully.');
      setApproveDialogOpen(false);
      await fetchRelayStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      setErrorMsg('Approval failed: ' + extractGatewayErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Submit Revocation
  const handleConfirmRevoke = async () => {
    if (!activeGateway?._id) return;
    setActionLoading(true);
    try {
      await revokeAuthRelayKey(activeGateway._id);
      setSuccessMsg('Relay key revoked successfully.');
      setRevokeDialogOpen(false);
      await fetchRelayStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      setErrorMsg('Revocation failed: ' + extractGatewayErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Readiness computation: Online AND Approved AND lastPollAt within 45s
  const readiness = useMemo(() => {
    if (!activeGateway) return { ready: false, reason: 'No active gateway selected' };
    const isOnline = String(activeGateway.status || '').toLowerCase() === 'online';
    const isApproved = activeGateway.relay?.status === 'approved';
    const lastPoll = activeGateway.relay?.lastPollAt ? new Date(activeGateway.relay.lastPollAt).getTime() : 0;
    const now = Date.now();
    const isPollingActive = lastPoll > 0 && now - lastPoll <= 45000;

    if (!isOnline) {
      return { ready: false, reason: 'Gateway is offline or degraded' };
    }
    if (!isApproved) {
      return { ready: false, reason: 'Relay key is not approved' };
    }
    if (!isPollingActive) {
      return { ready: false, reason: 'Gateway has not polled within the last 45s' };
    }
    return { ready: true, reason: 'Gateway online and actively polling' };
  }, [activeGateway]);

  const isRelayEnabled = relayData?.enabled !== false;
  const relayStatus = activeGateway?.relay?.status || 'unregistered';
  const fingerprint = activeGateway?.relay?.fingerprint;

  return (
    <Card
      elevation={0}
      data-testid="ad-login-relay-card"
      sx={{
        border: '1px solid var(--hairline, #E4E9F0)',
        borderRadius: '8px',
        mb: 3,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <VpnKeyIcon sx={{ color: 'var(--edge-blue, #1F6FEB)' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Active Directory Login Relay
            </Typography>
          </Stack>
        }
        subheader="Outbound long-poll relay for authenticating Active Directory credentials without inbound ports."
        action={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, mr: 1 }}>
            <Chip
              size="small"
              label={isRelayEnabled ? 'Relay Enabled' : 'Relay Disabled'}
              color={isRelayEnabled ? 'success' : 'default'}
              variant={isRelayEnabled ? 'filled' : 'outlined'}
              data-testid="relay-enabled-chip"
              sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
            />
            <Tooltip title="Refresh relay status">
              <span>
                <IconButton size="small" onClick={() => fetchRelayStatus()} disabled={loading} data-testid="refresh-relay-btn">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
        sx={{
          pb: 1,
          borderBottom: '1px solid var(--hairline, #E4E9F0)',
        }}
      />

      <CardContent sx={{ pt: 2 }}>
        {loading && !relayData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {errorMsg && (
              <Alert severity="error" onClose={() => setErrorMsg('')} data-testid="relay-error-alert">
                {errorMsg}
              </Alert>
            )}

            {successMsg && (
              <Alert severity="success" onClose={() => setSuccessMsg('')} data-testid="relay-success-alert">
                {successMsg}
              </Alert>
            )}

            {!isRelayEnabled && (
              <Alert severity="info" data-testid="relay-disabled-banner">
                AD Login Relay is currently disabled (<code>AD_AUTH_RELAY_ENABLED</code> is not enabled on the server).
              </Alert>
            )}

            {isRelayEnabled && (!relayData?.gateways || relayData.gateways.length === 0) ? (
              <Alert severity="warning">
                No gateways found. Enroll a gateway to enable outbound Active Directory login relay.
              </Alert>
            ) : null}

            {isRelayEnabled && activeGateway && (
              <>
                {/* Gateway Selector (if multiple) */}
                {relayData.gateways.length > 1 && (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="relay-gateway-select-label">Select Gateway</InputLabel>
                    <Select
                      labelId="relay-gateway-select-label"
                      value={selectedGatewayId}
                      label="Select Gateway"
                      onChange={(e) => setSelectedGatewayId(e.target.value)}
                    >
                      {relayData.gateways.map((gw) => (
                        <MenuItem key={gw._id} value={gw._id}>
                          {gw.name} ({gw.status})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Gateway & Readiness Overview */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.75,
                    backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F7F9FC',
                    borderColor: 'var(--hairline, #E4E9F0)',
                    borderRadius: '6px',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Gateway: <strong>{activeGateway.name}</strong>
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={`Status: ${activeGateway.status || 'Offline'}`}
                          color={String(activeGateway.status).toLowerCase() === 'online' ? 'success' : 'default'}
                          sx={{ fontSize: 11, height: 22 }}
                        />
                        <Chip
                          size="small"
                          label={readiness.ready ? 'Relay Active' : 'Relay Idle'}
                          color={readiness.ready ? 'success' : 'default'}
                          icon={readiness.ready ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                          data-testid="relay-readiness-chip"
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      </Stack>
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Readiness Status: {readiness.reason}
                    </Typography>

                    {activeGateway.relay?.lastPollAt && (
                      <Typography variant="caption" color="text.secondary">
                        Last Polling Activity:{' '}
                        <strong>{new Date(activeGateway.relay.lastPollAt).toLocaleString()}</strong>
                      </Typography>
                    )}
                  </Stack>
                </Paper>

                <Divider sx={{ my: 0.5 }} />

                {/* Key Status Section */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Encryption Public Key
                    </Typography>
                    {relayStatus === 'pending' && (
                      <Chip
                        size="small"
                        label="New key awaiting approval"
                        color="warning"
                        data-testid="relay-status-pending"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22 }}
                      />
                    )}
                    {relayStatus === 'approved' && (
                      <Chip
                        size="small"
                        label="Approved"
                        color="success"
                        data-testid="relay-status-approved"
                        sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
                      />
                    )}
                    {relayStatus === 'revoked' && (
                      <Chip
                        size="small"
                        label="Revoked"
                        color="error"
                        data-testid="relay-status-revoked"
                        sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
                      />
                    )}
                    {relayStatus === 'unregistered' && (
                      <Chip
                        size="small"
                        label="No Key Registered"
                        data-testid="relay-status-unregistered"
                        sx={{ fontSize: 11, height: 22 }}
                      />
                    )}
                  </Stack>

                  {relayStatus === 'pending' && (
                    <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="relay-pending-warning">
                      A new public key was registered by this gateway and requires administrator approval before the relay can process logins.
                    </Alert>
                  )}

                  {relayStatus === 'revoked' && (
                    <Alert severity="error" sx={{ mb: 1.5 }} data-testid="relay-revoked-alert">
                      The relay key has been revoked. Outbound login requests will fail closed until a new key is registered and approved.
                    </Alert>
                  )}

                  {fingerprint ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        backgroundColor: mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#FFFFFF',
                        borderColor: 'var(--hairline, #E4E9F0)',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          SHA-256 Fingerprint:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Consolas, monospace',
                            fontWeight: 600,
                            wordBreak: 'break-all',
                            fontSize: '0.8rem',
                          }}
                          data-testid="relay-fingerprint-text"
                        >
                          {fingerprint}
                        </Typography>
                      </Box>
                      <Tooltip title={copied ? 'Copied!' : 'Copy Fingerprint'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyFingerprint(fingerprint)}
                          data-testid="copy-fingerprint-btn"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Gateway has not yet generated and registered an RSA encryption keypair.
                    </Typography>
                  )}
                </Box>

                {/* Actions (Strictly permission gated) */}
                {canUpdate && (
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                    {relayStatus === 'pending' && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleOpenApproveDialog}
                          data-testid="approve-key-btn"
                        >
                          Approve Key
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => setRevokeDialogOpen(true)}
                          data-testid="reject-key-btn"
                        >
                          Reject
                        </Button>
                      </>
                    )}

                    {relayStatus === 'approved' && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setRevokeDialogOpen(true)}
                        data-testid="revoke-key-btn"
                      >
                        Revoke Key
                      </Button>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        )}
      </CardContent>

      {/* APPROVE KEY DIALOG */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !actionLoading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="approve-key-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Relay Public Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Approving this key authorizes the cloud backend to encrypt user authentication requests for this gateway.
            Please verify the fingerprint matches the one displayed in the on-premise gateway console.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Confirm Key Fingerprint"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={fingerprintInput}
            onChange={(e) => setFingerprintInput(e.target.value)}
            helperText="The fingerprint must match the registered key fingerprint."
            data-testid="approve-fingerprint-input"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmApprove}
            disabled={actionLoading || !fingerprintInput.trim()}
            data-testid="confirm-approve-btn"
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Approve Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* REVOKE KEY DIALOG */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => !actionLoading && setRevokeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="revoke-key-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Revoke Relay Key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke this relay key? The gateway will not be able to process Active Directory logins until a new key is registered and approved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={actionLoading}
            data-testid="confirm-revoke-btn"
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
